import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = typeof __VITE_SITE_URL__ !== 'undefined' ? __VITE_SITE_URL__ : 'https://yoybuy.boats';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'YOYBUY Sheet: Как находить трендовые товары из Китая с прибылью до 300%',
  description: 'Полное руководство по работе со спредшитами YOYBUY Sheet для поиска высокомаржинальных товаров из Китая. Таблицы поставщиков, сравнение цен и аналитика трендов 2026 года.',
  author: {
    '@type': 'Organization',
    name: 'Редакция YOYBUY',
    url: siteUrl,
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  publisher: {
    '@type': 'Organization',
    name: 'YOYBUY.boats',
    url: siteUrl,
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/yoybuy-sheet-guide`,
  },
  inLanguage: 'ru',
};

export default function ArticlePage() {
  const { t } = useTranslation();

  return (
    <main>
      <JsonLd data={articleJsonLd} />
      <nav className="sticky top-0 z-50 bg-background-50/90 backdrop-blur-md border-b border-background-200/70">
        <div className="flex items-center justify-between px-6 md:px-10 py-4 max-w-6xl mx-auto">
          <a href="/" className="font-heading text-xl font-bold text-foreground-900 tracking-tight whitespace-nowrap">
            YOYBUY<span className="text-primary-500">.boats</span>
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-700 px-4 py-2 rounded-full text-sm hover:border-primary-400 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>{t('btn_back_home')}</span>
          </a>
        </div>
      </nav>

      <article className="px-6 md:px-10 max-w-3xl mx-auto py-12 md:py-16" itemScope itemType="https://schema.org/Article">
        <meta itemProp="datePublished" content="2026-07-05" />
        <meta itemProp="author" content="Редакция YOYBUY" />
        <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 text-center leading-tight mb-6" itemProp="headline">
          {t('article_title')}
        </h1>

        <div className="flex items-center justify-center gap-4 text-sm text-foreground-500 mb-12 font-label">
          <span><i className="ri-calendar-line mr-1.5"></i>{t('article_meta_date')}</span>
          <span className="text-foreground-300">|</span>
          <span><i className="ri-user-line mr-1.5"></i>{t('article_meta_author')}</span>
        </div>

        <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
          <p className="mb-8">
            {t('article_intro_p1')}
          </p>

          <p className="mb-8">
            {t('article_intro_p2')}
          </p>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Почему спредшиты — это ключевой инструмент современного дропшиппера
          </h2>

          <p className="mb-6">
            Традиционный подход к поиску товаров из Китая требует десятков часов ручного исследования. Нужно открыть множество вкладок браузера, сравнивать цены на разных платформах — 1688, Taobao, Alibaba, проверять отзывы о поставщиках, оценивать минимальные партии заказа. Система <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">YOYBUY Sheet</a> автоматизирует этот процесс, предоставляя уже структурированные данные в удобном табличном формате.
          </p>

          <p className="mb-6">
            Каждый спредшит содержит проверенную информацию: название товара, категорию, цену от производителя, контакты поставщика, минимальный объём заказа, расчётную маржинальность при перепродаже на маркетплейсах вроде Amazon, eBay, Ozon и Wildberries. Это не просто список — это полноценный аналитический инструмент, который экономит до 70% времени на исследование рынка.
          </p>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Как работает система YOYBUY Sheet: пошаговое руководство
          </h2>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Шаг 1. Выбор категории товаров
          </h3>
          <p className="mb-6">
            На главной странице <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">yoybuy.boats</a> вы найдёте полный список категорий — от обуви и одежды до аксессуаров и парфюмерии. Каждая категория ведёт на соответствующий раздел с товарами на <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline font-medium">kaspreadsheet.com</a>. Система охватывает 14 основных категорий, которые покрывают практически весь спектр товаров для дропшиппинга.
          </p>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Шаг 2. Анализ спредшита
          </h3>
          <p className="mb-6">
            После выбора категории вы получаете доступ к таблице, которая содержит следующие столбцы: наименование продукта, цена в юанях, цена в долларах, рейтинг поставщика, минимальный заказ, расчётная маржа при перепродаже в США, Европе и России. Это позволяет мгновенно оценить потенциал каждого товара без дополнительных расчётов в Excel.
          </p>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Шаг 3. Сравнение и выбор
          </h3>
          <p className="mb-6">
            Одно из главных преимуществ системы — возможность быстро сравнивать аналогичные товары от разных поставщиков. <strong className="font-semibold text-foreground-900">YOYBUY Sheet</strong> агрегирует данные с нескольких китайских платформ, позволяя вам найти оптимальное соотношение цены и качества. Для каждой позиции указаны ключевые параметры, которые влияют на итоговую прибыль: вес товара, стоимость доставки, сезонность спроса.
          </p>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Трендовые категории 2026 года для дропшиппинга
          </h2>

          <p className="mb-6">
            На основе анализа данных из наших спредшитов, мы выделили несколько категорий, которые показывают наибольший рост спроса в 2026 году. Эти данные основаны на реальной статистике продаж и поисковых запросов на основных маркетплейсах.
          </p>

          <div className="overflow-x-auto my-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="text-left py-3 px-4 font-label font-semibold text-foreground-800 whitespace-nowrap">Категория</th>
                  <th className="text-left py-3 px-4 font-label font-semibold text-foreground-800 whitespace-nowrap">Рост спроса</th>
                  <th className="text-left py-3 px-4 font-label font-semibold text-foreground-800 whitespace-nowrap">Средняя маржа</th>
                  <th className="text-left py-3 px-4 font-label font-semibold text-foreground-800 whitespace-nowrap">Конкуренция</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200">
                  <td className="py-3 px-4 font-medium text-foreground-800">Обувь</td>
                  <td className="py-3 px-4 text-accent-600 font-semibold">+42%</td>
                  <td className="py-3 px-4">80–200%</td>
                  <td className="py-3 px-4 text-foreground-500">Средняя</td>
                </tr>
                <tr className="border-b border-background-200 bg-background-50">
                  <td className="py-3 px-4 font-medium text-foreground-800">Худи / Свитера</td>
                  <td className="py-3 px-4 text-accent-600 font-semibold">+35%</td>
                  <td className="py-3 px-4">100–250%</td>
                  <td className="py-3 px-4 text-foreground-500">Высокая</td>
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 px-4 font-medium text-foreground-800">Аксессуары</td>
                  <td className="py-3 px-4 text-accent-600 font-semibold">+28%</td>
                  <td className="py-3 px-4">150–300%</td>
                  <td className="py-3 px-4 text-foreground-500">Низкая</td>
                </tr>
                <tr className="border-b border-background-200 bg-background-50">
                  <td className="py-3 px-4 font-medium text-foreground-800">Сумки</td>
                  <td className="py-3 px-4 text-accent-600 font-semibold">+31%</td>
                  <td className="py-3 px-4">120–280%</td>
                  <td className="py-3 px-4 text-foreground-500">Средняя</td>
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 px-4 font-medium text-foreground-800">Парфюмерия</td>
                  <td className="py-3 px-4 text-accent-600 font-semibold">+25%</td>
                  <td className="py-3 px-4">200–400%</td>
                  <td className="py-3 px-4 text-foreground-500">Низкая</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Как максимизировать прибыль с помощью спредшитов
          </h2>

          <p className="mb-6">
            Ключевой принцип работы с <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">системой YOYBUY Sheet</a> — это не просто поиск дешёвых товаров, а выявление товаров с наибольшей разницей между закупочной ценой и рыночной стоимостью на целевых площадках. Наши спредшиты специально спроектированы так, чтобы выделять позиции с маржой от 150% и выше.
          </p>

          <p className="mb-6">
            Важно также учитывать сезонность. Например, худи и свитера показывают пик продаж в осенне-зимний период, а лёгкая одежда и аксессуары — весной и летом. Система спредшитов включает информацию о сезонности спроса, что помогает планировать закупки заранее и избегать затоваривания несезонными позициями.
          </p>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Часто задаваемые вопросы о YOYBUY Sheet
          </h2>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Нужно ли знать китайский язык для работы со спредшитами?
          </h3>
          <p className="mb-6">
            Нет. Все данные в спредшитах <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">YOYBUY Sheet</a> уже переведены и адаптированы. Названия товаров, описания, контакты поставщиков — всё представлено в удобном для восприятия формате. Вам не требуется специальных знаний или навыков работы с китайскими платформами.
          </p>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Как часто обновляются данные в спредшитах?
          </h3>
          <p className="mb-6">
            Данные обновляются ежедневно. Команда <strong className="font-semibold text-foreground-900">YOYBUY</strong> постоянно мониторит изменения цен, появление новых поставщиков и трендовых товаров. Это гарантирует, что вы всегда работаете с актуальной информацией, а не с устаревшими данными недельной или месячной давности.
          </p>

          <h3 className="text-lg font-heading font-semibold text-foreground-800 mt-8 mb-3">
            Подходит ли система для новичков в дропшиппинге?
          </h3>
          <p className="mb-6">
            Да, система специально спроектирована так, чтобы быть понятной даже для тех, кто никогда раньше не занимался закупками из Китая. Интерфейс интуитивен, а каждый спредшит сопровождается пояснениями и рекомендациями по использованию. На <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">yoybuy.boats</a> вы найдёте все необходимые категории товаров для старта.
          </p>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-5">
            Заключение: почему YOYBUY Sheet — это ваш инструмент для прибыльного дропшиппинга
          </h2>

          <p className="mb-8">
            В современном мире электронной коммерции информация — это самый ценный актив. Система спредшитов <strong className="font-semibold text-foreground-900">YOYBUY Sheet</strong> предоставляет вам именно то, что нужно для принятия правильных решений: структурированные, проверенные и ежедневно обновляемые данные о товарах из Китая. Независимо от того, являетесь ли вы опытным продавцом или только начинаете свой путь в дропшиппинге — наши инструменты помогут вам сократить время на исследование рынка и увеличить прибыль.
          </p>

          <p className="mb-8">
            Начните с выбора категории на главной странице <a href="/" className="text-primary-600 hover:text-primary-700 underline font-medium">yoybuy.boats</a> и убедитесь сами, насколько проще становится процесс поиска и анализа товаров с профессиональной системой спредшитов.
          </p>
        </div>

        <div className="mt-14 p-8 md:p-10 bg-background-900 rounded-2xl text-center">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
            Готовы начать закупки?
          </h3>
          <p className="text-foreground-300 text-sm mb-8 max-w-md mx-auto">
            Получите доступ к полной системе спредшитов YOYBUY Sheet и начните находить прибыльные товары уже сегодня.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-7 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <span>{t('btn_yoybuy_sheet')}</span>
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              <span>{t('btn_buy_now')}</span>
              <i className="ri-shopping-bag-line"></i>
            </a>
          </div>
        </div>

        <div className="mt-10 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-foreground-500 hover:text-primary-500 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>{t('btn_back_home')}</span>
          </a>
        </div>
      </article>

      <footer className="bg-background-900 text-background-50 border-t border-foreground-700/30">
        <div className="px-6 md:px-10 max-w-6xl mx-auto py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="/" className="font-heading text-lg font-bold text-white tracking-tight whitespace-nowrap">
              YOYBUY<span className="text-primary-300">.boats</span>
            </a>
            <div className="flex flex-wrap gap-4 justify-center">
              {categories.slice(0, 5).map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-xs text-foreground-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
            <p className="text-foreground-500 text-xs">
              &copy; {new Date().getFullYear()} YOYBUY.boats
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}